// Comprehensive world country list with ISO-2 codes, flag emojis, and demonyms.
// Used by the nationality / applying-from selectors and for displaying flags
// in eligibility banners. Demonyms cover the top ~80 most likely visa-applicant
// countries; for the rest we fall back to "<Country> national".

const flagFromCode = (code) => {
  if (!code || code.length !== 2) return "";
  const A = 127397; // 0x1F1E6 - 'A'
  return String.fromCodePoint(...code.toUpperCase().split("").map((c) => c.charCodeAt(0) + A));
};

// [name, ISO-2 code, demonym?]
const RAW = [
  ["Afghanistan","AF","Afghan"],["Albania","AL","Albanian"],["Algeria","DZ","Algerian"],
  ["Andorra","AD","Andorran"],["Angola","AO","Angolan"],["Argentina","AR","Argentinian"],
  ["Armenia","AM","Armenian"],["Australia","AU","Australian"],["Austria","AT","Austrian"],
  ["Azerbaijan","AZ","Azerbaijani"],["Bahrain","BH","Bahraini"],["Bangladesh","BD","Bangladeshi"],
  ["Belarus","BY","Belarusian"],["Belgium","BE","Belgian"],["Bolivia","BO","Bolivian"],
  ["Bosnia and Herzegovina","BA","Bosnian"],["Brazil","BR","Brazilian"],["Brunei","BN","Bruneian"],
  ["Bulgaria","BG","Bulgarian"],["Cambodia","KH","Cambodian"],["Cameroon","CM","Cameroonian"],
  ["Canada","CA","Canadian"],["Chile","CL","Chilean"],["China","CN","Chinese"],
  ["Colombia","CO","Colombian"],["Costa Rica","CR","Costa Rican"],["Croatia","HR","Croatian"],
  ["Cuba","CU","Cuban"],["Cyprus","CY","Cypriot"],["Czech Republic","CZ","Czech"],
  ["Denmark","DK","Danish"],["Dominican Republic","DO","Dominican"],["Ecuador","EC","Ecuadorian"],
  ["Egypt","EG","Egyptian"],["El Salvador","SV","Salvadoran"],["Estonia","EE","Estonian"],
  ["Ethiopia","ET","Ethiopian"],["Finland","FI","Finnish"],["France","FR","French"],
  ["Georgia","GE","Georgian"],["Germany","DE","German"],["Ghana","GH","Ghanaian"],
  ["Greece","GR","Greek"],["Guatemala","GT","Guatemalan"],["Honduras","HN","Honduran"],
  ["Hong Kong","HK","Hongkonger"],["Hungary","HU","Hungarian"],["Iceland","IS","Icelandic"],
  ["India","IN","Indian"],["Indonesia","ID","Indonesian"],["Iran","IR","Iranian"],
  ["Iraq","IQ","Iraqi"],["Ireland","IE","Irish"],["Israel","IL","Israeli"],
  ["Italy","IT","Italian"],["Japan","JP","Japanese"],["Jordan","JO","Jordanian"],
  ["Kazakhstan","KZ","Kazakh"],["Kenya","KE","Kenyan"],["Kuwait","KW","Kuwaiti"],
  ["Kyrgyzstan","KG","Kyrgyz"],["Laos","LA","Laotian"],["Latvia","LV","Latvian"],
  ["Lebanon","LB","Lebanese"],["Libya","LY","Libyan"],["Liechtenstein","LI","Liechtensteiner"],
  ["Lithuania","LT","Lithuanian"],["Luxembourg","LU","Luxembourgish"],["Malaysia","MY","Malaysian"],
  ["Maldives","MV","Maldivian"],["Malta","MT","Maltese"],["Mexico","MX","Mexican"],
  ["Moldova","MD","Moldovan"],["Monaco","MC","Monégasque"],["Mongolia","MN","Mongolian"],
  ["Montenegro","ME","Montenegrin"],["Morocco","MA","Moroccan"],["Myanmar","MM","Burmese"],
  ["Nepal","NP","Nepali"],["Netherlands","NL","Dutch"],["New Zealand","NZ","New Zealander"],
  ["Nigeria","NG","Nigerian"],["North Macedonia","MK","Macedonian"],["Norway","NO","Norwegian"],
  ["Oman","OM","Omani"],["Pakistan","PK","Pakistani"],["Palestine","PS","Palestinian"],
  ["Panama","PA","Panamanian"],["Paraguay","PY","Paraguayan"],["Peru","PE","Peruvian"],
  ["Philippines","PH","Filipino"],["Poland","PL","Polish"],["Portugal","PT","Portuguese"],
  ["Qatar","QA","Qatari"],["Romania","RO","Romanian"],["Russia","RU","Russian"],
  ["Rwanda","RW","Rwandan"],["Saudi Arabia","SA","Saudi"],["Senegal","SN","Senegalese"],
  ["Serbia","RS","Serbian"],["Singapore","SG","Singaporean"],["Slovakia","SK","Slovak"],
  ["Slovenia","SI","Slovenian"],["South Africa","ZA","South African"],["South Korea","KR","South Korean"],
  ["Spain","ES","Spanish"],["Sri Lanka","LK","Sri Lankan"],["Sudan","SD","Sudanese"],
  ["Sweden","SE","Swedish"],["Switzerland","CH","Swiss"],["Syria","SY","Syrian"],
  ["Taiwan","TW","Taiwanese"],["Tajikistan","TJ","Tajik"],["Tanzania","TZ","Tanzanian"],
  ["Thailand","TH","Thai"],["Tunisia","TN","Tunisian"],["Turkey","TR","Turkish"],
  ["Turkmenistan","TM","Turkmen"],["Uganda","UG","Ugandan"],["Ukraine","UA","Ukrainian"],
  ["United Arab Emirates","AE","Emirati"],["United Kingdom","GB","British"],
  ["United States","US","American"],["Uruguay","UY","Uruguayan"],["Uzbekistan","UZ","Uzbek"],
  ["Venezuela","VE","Venezuelan"],["Vietnam","VN","Vietnamese"],["Yemen","YE","Yemeni"],
  ["Zimbabwe","ZW","Zimbabwean"],
];

export const WORLD_COUNTRIES = RAW.map(([name, code, demonym]) => ({
  name, code, demonym, flag: flagFromCode(code),
}));

const BY_CODE = Object.fromEntries(WORLD_COUNTRIES.map((c) => [c.code, c]));
const BY_NAME = Object.fromEntries(WORLD_COUNTRIES.map((c) => [c.name, c]));

export function worldCountryByCode(code) {
  return BY_CODE[(code || "").toUpperCase()] || null;
}
export function worldCountryByName(name) {
  return BY_NAME[name] || null;
}
export function demonymForCode(code) {
  const c = worldCountryByCode(code);
  return c ? (c.demonym || `${c.name}`) : "";
}
export function flagForCode(code) {
  const c = worldCountryByCode(code);
  return c ? c.flag : "";
}

// ISO-2 codes for the 29 Schengen Area countries (incl. Bulgaria & Romania since Jan 2025, Croatia since 2023).
export const SCHENGEN_CODES = new Set([
  "AT","BE","BG","HR","CZ","DK","EE","FI","FR","DE","GR","HU","IS","IT","LV","LI","LT","LU","MT","NL","NO","PL","PT","RO","SK","SI","ES","SE","CH",
]);

// ISO-2 codes for nationalities that may enter the Schengen Area visa-free
// for up to 90 days within any 180-day period (Annex II / official EU list).
// Only codes that exist in WORLD_COUNTRIES are listed here — others will never
// be selectable in the dropdown anyway.
export const VISA_FREE_CODES = new Set([
  "AD","AR","AU","BA","BR","BN","CA","CL","CO","CR","SV","GE","GT","HN","HK",
  "IL","JP","MY","MX","MD","MC","ME","NZ","MK","PA","PY","PE","RS","SG","KR",
  "TW","UA","AE","GB","US","UY","VE",
]);

// Adjective form for the destination consulate label ("French Consulate in Saudi Arabia").
export const SCHENGEN_ADJECTIVE = {
  Austria: "Austrian", Belgium: "Belgian", Bulgaria: "Bulgarian", Croatia: "Croatian", "Czech Republic": "Czech", Denmark: "Danish",
  Estonia: "Estonian", Finland: "Finnish", France: "French", Germany: "German",
  Greece: "Greek", Hungary: "Hungarian", Iceland: "Icelandic", Italy: "Italian",
  Latvia: "Latvian", Liechtenstein: "Liechtenstein", Lithuania: "Lithuanian",
  Luxembourg: "Luxembourg", Malta: "Maltese", Netherlands: "Dutch", Norway: "Norwegian",
  Poland: "Polish", Portugal: "Portuguese", Romania: "Romanian", Slovakia: "Slovak", Slovenia: "Slovenian",
  Spain: "Spanish", Sweden: "Swedish", Switzerland: "Swiss",
};
